import { useState } from 'react';
import {
  authErrorDetail,
  describeAuthError,
  getRuntimeConfig,
  sendPasswordReset,
  signInWithGoogle,
  signInWithMicrosoft,
  signInWithPassword,
  signUpWithPassword,
} from '../lib/firebase';
import { Button, Field, Input } from '../components/ui';

/**
 * Sign in, sign up, or reset a password.
 *
 * Which methods appear is decided by the server (`providers` in /config.json),
 * not by this file. Firebase offers no way to ask which providers are enabled,
 * and a button for a disabled one fails with an opaque `auth/internal-error` —
 * the exact failure this deployment has already misdiagnosed three times. So the
 * deployment states what it supports and the UI shows only that.
 *
 * Password sign-in exists because not everyone is a Google shop: outside counsel
 * and audit firms are usually on Microsoft, and until an Entra app registration
 * is in place a password is the only way in for them. Microsoft SSO is the better
 * answer and appears here as soon as it is configured.
 *
 * Signing up grants no access to anything. Authorization is company_members in
 * Postgres, so a brand-new account with no invitation waiting sees an empty
 * company list. That is what makes open signup safe, and it is why the copy says
 * so rather than implying an account is worth having on its own.
 */

const MODES = {
  signin: { title: 'Sign in to view and manage your equity.', submit: 'Sign in' },
  signup: { title: 'Create an account to accept your invitation.', submit: 'Create account' },
  reset: { title: 'We will email you a link to set a new password.', submit: 'Send reset link' },
};

/**
 * `providers` is injectable so the render test can exercise every combination
 * without a mock or a fetch — the same reason the domain layer takes result sets
 * instead of issuing its own queries. In the app it always comes from
 * /config.json; the Google-only fallback is for the window before it loads.
 */
export default function SignIn({ providers: providersOverride = null }) {
  const providers = providersOverride ?? getRuntimeConfig()?.providers ?? { google: true };
  const [mode, setMode] = useState('signin');
  const [form, setForm] = useState({ email: '', password: '', displayName: '' });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notice, setNotice] = useState(null);

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }));

  function fail(err) {
    setError(describeAuthError(err));
    setDetail(authErrorDetail(err));
    console.error('[signin] failed', err);
  }

  function begin(which) {
    setBusy(which);
    setError(null);
    setDetail(null);
    setNotice(null);
  }

  async function withGoogle() {
    begin('google');
    try {
      await signInWithGoogle();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  async function withMicrosoft() {
    begin('microsoft');
    try {
      await signInWithMicrosoft();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  async function submit(event) {
    event.preventDefault();
    begin('password');
    try {
      if (mode === 'signin') {
        await signInWithPassword(form.email, form.password);
        // No navigation: App watches auth state and re-renders.
      } else if (mode === 'signup') {
        await signUpWithPassword(form.email, form.password, form.displayName || null);
        // App's verification gate takes over from here — the account exists but
        // the API refuses unverified addresses.
      } else {
        await sendPasswordReset(form.email);
        // Deliberately not "we found your account": whether an address is
        // registered is not something an unauthenticated caller should learn.
        setNotice(`If an account exists for ${form.email}, a reset link is on its way.`);
        setMode('signin');
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  }

  const showPasswordForm = providers.password && (mode !== 'signup' || providers.signup);
  const showSso = providers.google || providers.microsoft;
  const copy = MODES[mode];

  return (
    <div className="signin-wrap">
      <div className="signin-card">
        <div className="signin-brand">
          <span className="brand-dot" aria-hidden="true" />
          Sevah Cap Table
        </div>
        <p className="signin-sub">{copy.title}</p>

        {notice ? <div className="banner banner-good">{notice}</div> : null}

        {error ? (
          <div className="banner banner-error" role="alert">
            {error}
            {detail ? (
              <details className="signin-detail">
                <summary>Technical detail</summary>
                <pre>{detail}</pre>
              </details>
            ) : null}
          </div>
        ) : null}

        {showSso && mode !== 'reset' ? (
          <div className="signin-sso">
            {providers.google ? (
              <Button
                variant="primary"
                loading={busy === 'google'}
                disabled={busy && busy !== 'google'}
                onClick={withGoogle}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Continue with Google
              </Button>
            ) : null}
            {providers.microsoft ? (
              <Button
                loading={busy === 'microsoft'}
                disabled={busy && busy !== 'microsoft'}
                onClick={withMicrosoft}
                style={{ width: '100%', justifyContent: 'center' }}
              >
                Continue with Microsoft
              </Button>
            ) : null}
          </div>
        ) : null}

        {showSso && showPasswordForm && mode !== 'reset' ? (
          <div className="signin-divider">or</div>
        ) : null}

        {showPasswordForm ? (
          <form onSubmit={submit}>
            {mode === 'signup' ? (
              <Field label="Name">
                <Input
                  value={form.displayName}
                  onChange={set('displayName')}
                  autoComplete="name"
                  placeholder="Optional"
                />
              </Field>
            ) : null}

            <Field
              label="Email"
              required
              hint={
                mode === 'signup'
                  ? 'Use the address your invitation was sent to — access is tied to it.'
                  : undefined
              }
            >
              <Input
                type="email"
                value={form.email}
                onChange={set('email')}
                autoComplete="username"
                required
                autoFocus={!showSso}
              />
            </Field>

            {mode !== 'reset' ? (
              <Field
                label="Password"
                required
                hint={mode === 'signup' ? 'At least 8 characters.' : undefined}
              >
                <Input
                  type="password"
                  value={form.password}
                  onChange={set('password')}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  minLength={mode === 'signup' ? 8 : undefined}
                  required
                />
              </Field>
            ) : null}

            <Button
              type="submit"
              variant={showSso ? 'secondary' : 'primary'}
              loading={busy === 'password'}
              disabled={busy && busy !== 'password'}
              style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}
            >
              {copy.submit}
            </Button>
          </form>
        ) : null}

        {showPasswordForm ? (
          <div className="signin-links">
            {mode !== 'signin' ? (
              <button type="button" className="link-button" onClick={() => setMode('signin')}>
                Back to sign in
              </button>
            ) : (
              <>
                <button type="button" className="link-button" onClick={() => setMode('reset')}>
                  Forgot password?
                </button>
                {providers.signup ? (
                  <button type="button" className="link-button" onClick={() => setMode('signup')}>
                    Create an account
                  </button>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        <p className="muted signin-foot">
          {providers.signup
            ? 'Creating an account does not grant access to a company on its own — an administrator invites you, and the invitation activates the first time you sign in.'
            : 'Accounts are managed centrally. If you have not been invited, ask an administrator for access first.'}
        </p>
      </div>
    </div>
  );
}
